/**
 * @fileoverview ステップ管理コンポーネント
 * @description
 *    （画面遷移を挟まず）同一画面内でステップ別に画面表示を切り替える場合に使用する。
 *    各ステップ内容を slot コンテンツとして注入。
 *    暫定的に最大 10 ステップ分までコンポーネントとしては用意しているので、もし 10 ステップ以上必要になった場合はコンポーネント側に追加対応する。
 */
<script lang="ts">
import {
  defineComponent,
  useSlots,
  cloneVNode,
  isVNode,
  ref,
  computed,
  watchEffect,
  onMounted,
  nextTick,
  type Ref,
  type VNode,
} from 'vue'
import VueScrollTo from 'vue-scrollto'
import { useScreenLockStore } from '@/stores/screenLock'
import wait from '@/utilities/wait'

export default defineComponent({
  name: 'StepWizard',

  props: {
    /**
     * ステップ遷移時にページ上端へ自動スクロールを行うかどうか
     */
    scrollByStep: {
      type: Boolean,
      default: true,
    },

    /**
     * ステップ遷移時にスクリーンロックを行うかどうか
     */
    screenLockByStep: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    // constants
    const TRANSITION_DURATION_SECONDS = 0.6 as const

    // stores
    const screenLockStore = useScreenLockStore()

    const slots = useSlots()

    // data
    // DOM 取得用
    const rootEl = ref()
    const stepEl: Ref<Array<any>> = ref([])

    // 現在ステップの index 番号
    const currentStepIndex = ref(0)

    // ラッパー DOM の CSS の height 設定値用（transition アニメーション中は具体値が入り、transition 完了次第 auto に戻る）
    const wrapperHeight = ref('auto')

    // current 以外の子要素は current の高さを超えないよう overflow 制御をするため、その max-height 値を確保
    const childHeight = ref('auto')

    // トランジションアニメーション中かどうか
    const isTransitioning = ref(false)

    // レンダリング完了フラグ
    const rendered = ref(false)

    // 注入された slot コンテンツの VNode 配列
    const pages = ref<VNode[]>([])

    /**
     * 現在のステップ
     * @description コンポーネント内では使用していないが、親側から呼び出して使用するために定義している。
     */
    const currentStep = computed<number>(() => {
      return currentStepIndex.value + 1
    })

    /**
     * 有効なステップ数
     */
    const maxStep = computed<number>(() => {
      if (!rendered.value) return 0
      return pages.value ? pages.value.length : 0
    })

    const isLastStep = computed<boolean>(() => {
      return currentStepIndex.value === (maxStep.value - 1)
    })

    /**
     * slot として注入された各ステップのページデータコンポーネントを VNode の配列として取得
     *
     * @description コンポーネントの呼び出し元から注入された default slot を VNode 配列として取得
     * @description
     *  slot で渡されるコンテンツが素の HTML の場合、
     *  即座に VNODE として生成されるが、コンポーネントであった場合、
     *  slot が評価されるタイミングで初めて VNode が生成されるため、
     *  呼び出しのタイミングではまだ VNODE が生成されておらず、
     *  結果的に slot を評価した結果として生成された VNode が取得できない。
     *  そのため、 cloneVNode を使用して VNode を複製し、
     *  まだ評価されていないコンポーネントが VNode として確実に取得できるようにしている。
     * @description また、computed 等だと、途中で slot の中身が変更あった際にその変更が拾えないため、wachEffect で画面内の変更をすべて拾い、都度 slot を取得し直す。
     */
    watchEffect(() => {
      const children = slots.default?.() ?? []
      pages.value = children
        .filter(vnode =>
          vnode &&
          isVNode(vnode) &&
          typeof vnode.type !== 'symbol' // v-if でレンダリングしていない分を弾く
        )
        .map((vnode, i) => cloneVNode(vnode, { key: vnode.key ?? i }))
    })

    onMounted(() => {
      nextTick(async () => {
        rendered.value = true

        // root エレメント取得のため、DOM のレンダリング待機を挟む
        await nextTick()

        // 先頭 index のステップの高さで初期化
        updateHeight(0)

        if (!rootEl.value) {
          wrapperHeight.value = '0px'
          childHeight.value = wrapperHeight.value
        } else {
          // updateHeight 内の nextTick 完了後に、トランジション後と同じ状態に戻す
          await nextTick()
          onTransitionEnd()
        }
      })
    })

    const getStateClassNameByStep = (targetStepIndex: number): string => {
      if (targetStepIndex === currentStepIndex.value) return 'is-current'
      if (targetStepIndex === currentStepIndex.value - 1) return 'is-prev'
      if (targetStepIndex === currentStepIndex.value + 1) return 'is-next'

      return ''
    }

    const next = async () => {
      if (currentStepIndex.value >= (maxStep.value - 1)) {
        return
      }

      updateHeight(currentStepIndex.value + 1)
      currentStepIndex.value++

      if (props.scrollByStep) await VueScrollTo.scrollTo('#app')
      if (props.screenLockByStep) await handleScreenLock()
      await wait(TRANSITION_DURATION_SECONDS)
      onTransitionEnd()
    }

    const prev = async () => {
      if (currentStepIndex.value <= 0) {
        return
      }

      updateHeight(currentStepIndex.value - 1)
      currentStepIndex.value--

      if (props.scrollByStep) await VueScrollTo.scrollTo('#app')
      if (props.screenLockByStep) await handleScreenLock()
      await wait(TRANSITION_DURATION_SECONDS)
      onTransitionEnd()
    }

    /**
     * 連打防止のため、トランジションアニメーション中はスクリーンロックをかける
     */
    const handleScreenLock = async () => {
      screenLockStore.lockScreen({
        hasBlackOut: false,
        hasBlur: false,
        hasWaitAnimation: false,
        hasScrollLock: false,
      })

      await wait(TRANSITION_DURATION_SECONDS)
      screenLockStore.unlockScreen()
    }

    /**
     * ステップ遷移時の高さ更新
     * @param toIndex 遷移後のステップの index 番号
     */
    const updateHeight = (toIndex: number) => {
      if (rootEl.value) {
        wrapperHeight.value = rootEl.value.offsetHeight + 'px'  // 先に現ステップの高さを確保
        childHeight.value = wrapperHeight.value
        isTransitioning.value = true

        // 描画サイクル更新後に、確保している wrapper の高さを次のステップの高さに変更
        nextTick(() => {
          if (stepEl.value && stepEl.value[toIndex]) {
            wrapperHeight.value = stepEl.value[toIndex].scrollHeight + 'px'
            childHeight.value = wrapperHeight.value
          }
        })
      }
    }

    /**
     * ステップ遷移トランジションアニメーション後の後置処理
     */
    const onTransitionEnd = () => {
      if (isTransitioning.value) {
        wrapperHeight.value = 'auto'
        isTransitioning.value = false
      }
    }

    return {
      // constants
      TRANSITION_DURATION_SECONDS,

      // data
      stepEl,
      rootEl,
      pages,
      currentStepIndex,
      wrapperHeight,
      childHeight,
      isTransitioning,
      rendered,

      // computed
      maxStep,
      currentStep,
      isLastStep,

      // methods
      getStateClassNameByStep,
      next,
      prev,
    }
  },
})
</script>

<template>
  <section
    v-if="rendered"
    ref="rootEl"
    :class="[
      'f-section__stepWrapper',
      { 'is-transitioning': isTransitioning }
    ]"
    :style="{
      'height': wrapperHeight,
      '--transition-duration': `${TRANSITION_DURATION_SECONDS}s`
    }">
    <div
      v-for="(page, index) in pages"
      :key="index"
      :class="[
        'f-section__stepWrapper--body',
        getStateClassNameByStep(index),
      ]"
      :style="{
        '--max-height': index === currentStepIndex
          ? 'auto'
          : childHeight,
      }"
      :data-step="index + 1">
      <div
        :ref="el => { stepEl[index] = el }"
        class="div">
        <component
          :is="page"
          :key="index" />
      </div>
    <!-- /.f-section__stepWrapper body --></div>
  <!-- /.f-section__stepWrapper --></section>
</template>

<style lang="sass" scoped>
// foundation
.f-section__stepWrapper
  $root: &

  // JS 側の定数の方で設定（template の style 属性で渡している）
  --transition-duration: 0

  display: grid
  grid-template-columns: 1fr
  grid-template-rows: 1fr
  position: relative
  +transition($duration: var(--transition-duration))

  &.is-transitioning
    overflow: hidden

  & &--body
    --max-height: auto
    min-width: 0
    max-height: var(--max-height)
    grid-column: 1 / 2
    grid-row: 1 / 2
    position: absolute
    width: 100%
    top: 0
    left: 0
    opacity: 0
    visibility: hidden
    +transition($duration: var(--transition-duration))

    &.is-current
      position: relative
      left: 0
      opacity: 1
      visibility: visible

    &.is-prev
      transform: translateX(-5%)

    &.is-next
      transform: translateX(5%)

    &:not(.is-current)
      overflow: hidden
</style>
